import React, { createContext, useContext, useCallback, ReactNode, FC } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useApolloClient } from '@apollo/client';

import { CallActionPayload, MethodServiceType, MethodResponse } from '../types';

import { useImpactAiContext } from './ImpactAiContextProvider';
import { resolvePayload } from './helpers/resolvePayload';
import { navigate } from './methods/navigate';
import { setFormFields } from './methods/setFormFields';
import { batchGenerate } from './methods/batchGenerate';
import { ImpactAiTracker } from './useImpactAiTracking';
import { loadNonprofits } from './methods/loadNonprofits';
import { setContext } from './methods/setContext';
import { setDefaultPicture } from './methods/setDefaultPicture';
import { loadCauses } from './methods/loadCauses';
import { loadPictures } from './methods/loadPictures';
import { loadUser } from './methods/loadUser';
import { debug } from './methods/debug';
import { waitForContext } from './methods/waitForContext';
import { searchNonprofits } from './methods/searchNonprofits';
import { parseData } from './methods/parseData/parseData';
import { redux } from './methods/redux';

interface MethodContextType {
  methodService: MethodServiceType;
}

const MethodContext = createContext<MethodContextType | undefined>(undefined);

interface MethodProviderProps {
  children: ReactNode;
}

export const MethodProvider: FC<MethodProviderProps> = ({ children = {} }) => {
  const history = useHistory();
  const client = useApolloClient();
  const contextApi = useImpactAiContext();
  const dispatch = useDispatch();

  const methodService: MethodServiceType = useCallback(
    async (
      methodName: string,
      payload: CallActionPayload,
      uiStateCallback?,
      tracker?: ImpactAiTracker
    ): Promise<MethodResponse | void> => {
      if (methodName === 'delay') {
        const { delay } = payload as { delay: number };
        await new Promise((resolve) => setTimeout(resolve, delay));
        return {};
      }

      if (methodName === 'navigate' && history) {
        const resolvedPayload = resolvePayload(payload, contextApi.getFullContext());
        return navigate(resolvedPayload, history);
      }

      if (methodName === 'set-form-fields') {
        const resolvedPayload = resolvePayload(payload, contextApi.getFullContext());
        return setFormFields(resolvedPayload, dispatch);
      }

      if (methodName === 'set-default-picture') {
        // @NOTE-VZ: this is a temporary solution to set the default picture
        const resolvedPayload = resolvePayload(payload, contextApi.getFullContext());
        const pictures = contextApi.getValue('pictures');
        const categories = resolvedPayload.categories as string[];

        // Type check and cast the context values
        if (!Array.isArray(pictures)) {
          throw new Error('Pictures must be loaded before setting default picture');
        }

        // Cast pictures to the expected type since we know they're Picture objects from loadPictures
        const typedPictures = pictures as Array<{
          id: string;
          resource: string;
          categories: string[];
        }>;

        return setDefaultPicture(
          typedPictures,
          resolvedPayload.form as string,
          categories,
          dispatch
        );
      }

      if (methodName === 'batch-generate') {
        const resolvedPayload = resolvePayload(payload, contextApi.getFullContext());
        return batchGenerate(
          resolvedPayload,
          client,
          contextApi,
          dispatch,
          uiStateCallback,
          tracker
        );
      }

      if (methodName === 'set-context') {
        const resolvedPayload = resolvePayload(payload, contextApi.getFullContext());
        return setContext(resolvedPayload, contextApi);
      }

      if (methodName === 'load-nonprofits') {
        const resolvedPayload = resolvePayload(payload, contextApi.getFullContext());
        return loadNonprofits(resolvedPayload, client, contextApi, dispatch, uiStateCallback);
      }

      if (methodName === 'load-causes') {
        const resolvedPayload = resolvePayload(payload, contextApi.getFullContext());
        return loadCauses(resolvedPayload, client, contextApi, dispatch, uiStateCallback);
      }

      if (methodName === 'load-pictures') {
        const resolvedPayload = resolvePayload(payload, contextApi.getFullContext());
        return loadPictures(resolvedPayload, client, contextApi, dispatch, uiStateCallback);
      }

      if (methodName === 'load-user') {
        const resolvedPayload = resolvePayload(payload, contextApi.getFullContext());
        return loadUser(resolvedPayload, client, contextApi, dispatch, uiStateCallback);
      }

      if (methodName === 'wait-for-context') {
        const resolvedPayload = resolvePayload(payload, contextApi.getFullContext());
        return waitForContext(resolvedPayload, client, contextApi, dispatch, uiStateCallback);
      }

      if (methodName === 'debug') {
        const resolvedPayload = resolvePayload(payload, contextApi.getFullContext());
        return debug(resolvedPayload, contextApi, uiStateCallback);
      }

      if (methodName === 'search-nonprofits') {
        const resolvedPayload = resolvePayload(payload, contextApi.getFullContext());
        return searchNonprofits(resolvedPayload, client, contextApi, dispatch, uiStateCallback);
      }

      if (methodName === 'parse-data') {
        const resolvedPayload = resolvePayload(payload, contextApi.getFullContext());
        return parseData(resolvedPayload, contextApi, uiStateCallback);
      }

      if (methodName === 'redux') {
        const resolvedPayload = resolvePayload(payload, contextApi.getFullContext());
        return redux(resolvedPayload, contextApi, dispatch);
      }

      throw new Error(`Method ${methodName} not found`);
    },
    [history, client, contextApi, dispatch]
  );

  return <MethodContext.Provider value={{ methodService }}>{children}</MethodContext.Provider>;
};

export const useMethods = (): MethodContextType => {
  const context = useContext(MethodContext);

  if (!context) {
    throw new Error('useMethods must be used within a MethodProvider');
  }

  return context;
};
